import { HeartHandshake, Shield, Home as HomeIcon, Users, HandHeart, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const TEAM = [
  {
    name: "Daniel Yannick Ndong-Assa",
    role: "Founder & CEO",
    img: "https://customer-assets.emergentagent.com/job_senior-care-support/artifacts/cgwmde3n_4E402BCB-A5BE-4BC9-8E04-1612BD222195.JPG",
    bio: "Daniel started Melo Hopeful Care after seeing first-hand how hard it is for families to find warm, dependable everyday help. He leads the agency's vision, partnerships and caregiver culture.",
  },
  {
    name: "Rebecca Anderson",
    role: "Co-Founder & Director of Care",
    img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_senior-care-support/artifacts/ollcpmx6_Becca%20proffessional.png",
    bio: "Rebecca's passion for caring for others is deeply rooted in her personal journey. Growing up in the foster care system, she experienced significant hardship and trauma. Rather than allowing those experiences to define her, Rebecca chose to turn them into purpose: helping others who are in need.",
  },
];

const VALUES = [
  { icon: HeartHandshake, t: "Dignity", d: "Every person is honored — their voice, choices, and story come first." },
  { icon: HandHeart, t: "Compassion", d: "We lead with warmth. Presence is a practice, not a performance." },
  { icon: Shield, t: "Safety", d: "Background checks, training and clear protocols keep loved ones protected." },
  { icon: Sparkles, t: "Reliability", d: "Show-up-on-time care. Families can plan around us, not around gaps." },
  { icon: Users, t: "Family-Centered", d: "Care plans adapt to real family life — and we communicate often." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="section bg-gradient-to-b from-slate-50 to-white">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow">About Melo Hopeful Care</div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tight text-slate-900 leading-[1.05]">
              Care built on <span className="brand-gradient-text">hope, dignity and home.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Melo Hopeful Care was founded on a simple belief: aging — or recovering — shouldn't mean leaving the place you love.
              With compassionate caregivers, careful planning, and a family-first approach, we help people stay home safely and with joy.
            </p>
          </div>
          <div className="lg:col-span-5 relative rounded-[28px] overflow-hidden shadow-xl">
            <img src="https://images.unsplash.com/photo-1770134223774-13b735e29201?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Compassionate professional" className="w-full h-[460px] object-cover" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <div className="eyebrow">Our mission</div>
            <h2 className="mt-3 text-3xl font-semibold text-slate-900">To deliver compassionate, respectful, dependable in-home support — so every family feels cared for.</h2>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {VALUES.map(({ icon: Icon, t, d }) => (
              <div key={t} className="soft-card" data-testid={`value-${t.toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center">
                  <Icon className="w-5 h-5 text-white" strokeWidth={1.75} />
                </div>
                <div className="mt-5 font-heading font-semibold text-slate-900 text-lg">{t}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-slate-50" data-testid="about-founder">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1 relative rounded-[28px] overflow-hidden shadow-xl group">
            <img
              src="https://customer-assets.emergentagent.com/job_senior-care-support/artifacts/cgwmde3n_4E402BCB-A5BE-4BC9-8E04-1612BD222195.JPG"
              alt="Daniel Yannick Ndong-Assa, Founder & CEO of Melo Hopeful Care"
              className="w-full h-[520px] object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]"
              data-testid="founder-photo"
            />
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#072A47]/85 via-[#072A47]/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white" data-testid="founder-caption">
              <div className="text-xs uppercase tracking-[0.22em] text-teal-200 font-semibold">Founder & CEO</div>
              <div className="mt-1.5 font-heading font-semibold text-2xl leading-tight">Daniel Yannick Ndong-Assa</div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="eyebrow">Founder's story</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Built by family. Led with purpose.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Founder & CEO <span className="font-semibold text-slate-900">Daniel Yannick Ndong-Assa</span> started Melo Hopeful Care around a kitchen
              table after a loved one came home from a long hospital stay. The family learned first-hand how hard it is to find warm, reliable,
              everyday help — and how much that help changes a recovery.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Today, under Daniel's leadership, our team trains and places caregivers across Omaha and the Kansas City metro with the same care we'd
              want for our own parents: steady, skilled, kind, and there when they say they'll be there.
            </p>
            <blockquote className="mt-7 border-l-4 border-teal-500 pl-5 italic text-slate-700">
              "We named the company 'Hopeful' on purpose. Hope is what we bring through the door every single shift."
              <footer className="mt-2 not-italic text-sm text-slate-500">— Daniel Yannick Ndong-Assa, Founder & CEO</footer>
            </blockquote>
            <Link to="/request-care" className="btn-primary mt-8 inline-flex" data-testid="about-request-care-btn">Start your care plan</Link>
          </div>
        </div>
      </section>

      <section className="section" data-testid="about-leadership">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="eyebrow">Leadership team</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">The people behind the care.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Hands-on leaders who know every caregiver by name and every family by story.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-8">
            {TEAM.map((m) => (
              <div key={m.name} className="soft-card p-0 overflow-hidden group" data-testid={`team-${m.name.split(" ")[0].toLowerCase()}`}>
                <div className="relative">
                  <img src={m.img} alt={`${m.name}, ${m.role}`} className="w-full h-[420px] object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]" />
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#072A47]/85 to-transparent" />
                  <div className="absolute bottom-5 left-6 right-6 text-white">
                    <div className="text-xs uppercase tracking-[0.22em] text-teal-200 font-semibold">{m.role}</div>
                    <div className="mt-1 font-heading font-semibold text-2xl leading-tight">{m.name}</div>
                  </div>
                </div>
                <p className="p-6 text-sm text-slate-600 leading-relaxed">{m.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
